import {
  Card, Stack, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FC, useMemo } from 'react';
import { AnalyticItem } from '@mgm/common/components';
import ScrollBar from '@mgm/common/components/ScrollBar';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../../store/common/selectors';

type CommissionModel = 'split' | 'duplicate' | 'team steps';

type TTeamStat = {
  id: number;
  name: string;
  model: CommissionModel;
  netSales: number;
  commission: number;
  memberCount: number;
  totalOrders: number
  productsSold: number
};

type TTeamTotalsCard = {
  selectedTeamId: number,
  teamStats: Array<TTeamStat>
};

const TeamTotalsCard: FC<TTeamTotalsCard> = ({ selectedTeamId, teamStats }) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const currency = useAppSelector(getCurrencyByCode);

  const totals = useMemo(() => {
    if (selectedTeamId === 0) {
      return teamStats.reduce(
        (acc, team) => {
          acc.salesTotal += team.netSales;
          acc.totalOrders += team.totalOrders;
          acc.productsTotal += team.productsSold;
          return acc;
        },
        { salesTotal: 0, totalOrders: 0, productsTotal: 0 },
      );
    }

    const selectedTeam = teamStats.find((t) => t.id === selectedTeamId);
    return selectedTeam
      ? {
        salesTotal: selectedTeam.netSales,
        totalOrders: selectedTeam.totalOrders,
        productsTotal: selectedTeam.productsSold,
      }
      : { salesTotal: 0, totalOrders: 0, productsTotal: 0 };
  }, [selectedTeamId, teamStats]);

  return (
    <Card sx={{ height: '88px' }}>
      <ScrollBar>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          sx={{ p: 2 }}
        >
          <AnalyticItem
            title={t('sales')}
            percent={100}
            price={`${(totals.salesTotal || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${currency().mark}`}
            icon="pepicons-print:coins"
            color="#FF5C8D"
          />
          <AnalyticItem
            title={t('ordersFulfilled', { ns: 'common' })}
            percent={100}
            price={totals.totalOrders || 0}
            icon="solar:bag-3-linear"
            color="#845EC2"
          />
          <AnalyticItem
            title={t('productsSold')}
            percent={100}
            price={totals.productsTotal || 0}
            icon="fluent-mdl2:product-variant"
            color="#FFC75F"
          />
        </Stack>
      </ScrollBar>
    </Card>
  );
};

export { TeamTotalsCard };
